import { useEffect } from "react";
import { Link2, ListChecks, Trash2 } from "lucide-react";
import { LGoButton, LGoDayPicker, LGoSheet } from "@/ui/branded";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/ui/primitives/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { TimePicker } from "@/ui/primitives/time-picker";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/time";
import { czechPlural, offerForms } from "@/lib/czech";
import { saleCloseOf } from "@/domain/activity/rules";
import { dayKey, endsNextDay, type PlannedOffer } from "@/domain/offer/series";
import { useOfferSeries, rowTime } from "@/features/offers/hooks/useOfferSeries";
import type { OfferDraft } from "@/features/offers/hooks/useOfferForm";
import type { OfferActivityOption } from "@/data/repos/partnerOffersRepo";
import type { PartnerOffer } from "@/domain/offer/types";
import { FieldGroup } from "./FieldGroup";
import { offerSeriesCopy } from "@/features/offers/offer-authoring-copy";

const weekdayChoices = [
  { day: 1, label: "Po" },
  { day: 2, label: "Út" },
  { day: 3, label: "St" },
  { day: 4, label: "Čt" },
  { day: 5, label: "Pá" },
  { day: 6, label: "So" },
  { day: 0, label: "Ne" },
];

interface OfferSeriesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activities: OfferActivityOption[];
  publishedOffers: PartnerOffer[];
  havePublishedOffersLoaded: boolean;
  onSubmit: (drafts: OfferDraft[]) => Promise<OfferDraft[] | void> | void;
  isLoading?: boolean;
}

export function OfferSeriesDialog({
  open,
  onOpenChange,
  activities,
  publishedOffers,
  havePublishedOffersLoaded,
  onSubmit,
  isLoading,
}: OfferSeriesDialogProps) {
  const {
    form,
    template,
    rows,
    refusal,
    refusedRowIds,
    canDescribePattern,
    buildPreview,
    correctRow,
    removeRow,
    keepUnpublishedRows,
    reset,
    buildDrafts,
  } = useOfferSeries(activities, publishedOffers, havePublishedOffersLoaded);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  const canSubmit = rows !== null && rows.length > 0 && refusal === null;
  const [patternStartTime, patternEndTime] = form.watch(["start_time", "end_time"]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const unpublished = await onSubmit(buildDrafts());
    if (unpublished && unpublished.length > 0) keepUnpublishedRows(unpublished);
  };

  const toggleWeekday = (day: number, chosen: number[]) =>
    chosen.includes(day) ? chosen.filter((chosenDay) => chosenDay !== day) : [...chosen, day];

  return (
    <LGoSheet
      open={open}
      onOpenChange={onOpenChange}
      title={offerSeriesCopy.title}
      description={offerSeriesCopy.description}
      width="lg"
      footer={
        <>
          {refusal && (
            <p role="alert" className="mr-auto text-sm text-destructive">
              {refusal}
            </p>
          )}
          <LGoButton
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Zrušit
          </LGoButton>
          <LGoButton
            type="button"
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            loading={isLoading}
          >
            {rows === null
              ? "Vypsat termíny"
              : `Vypsat ${rows.length} ${czechPlural(rows.length, offerForms)}`}
          </LGoButton>
        </>
      }
    >
      <Form {...form}>
        <div className="space-y-6">
          <div className="space-y-3">
            <FormField
              control={form.control}
              name="activity_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{offerSeriesCopy.templateLabel}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={offerSeriesCopy.templatePlaceholder} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {activities.map((activity) => (
                        <SelectItem key={activity.id} value={String(activity.id)}>
                          {activity.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {template && <InheritedFromTemplate template={template} />}
          </div>

          <div className="space-y-3">
            <FormField
              control={form.control}
              name="weekdays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{offerSeriesCopy.weekdaysLabel}</FormLabel>
                  <div className="flex flex-wrap gap-2">
                    {weekdayChoices.map(({ day, label }) => (
                      <LGoButton
                        key={day}
                        type="button"
                        variant={field.value.includes(day) ? "primary" : "secondary"}
                        className="w-12"
                        aria-pressed={field.value.includes(day)}
                        onClick={() => field.onChange(toggleWeekday(day, field.value))}
                      >
                        {label}
                      </LGoButton>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="start_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{offerSeriesCopy.slotStartLabel}</FormLabel>
                    <FormControl>
                      <TimePicker
                        ariaLabel={offerSeriesCopy.slotStartLabel}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="end_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{offerSeriesCopy.slotEndLabel}</FormLabel>
                    <FormControl>
                      <TimePicker
                        ariaLabel={offerSeriesCopy.slotEndLabel}
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    {endsNextDay(patternStartTime, patternEndTime) && (
                      <FormDescription>{offerSeriesCopy.patternEndsNextDay}</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="range_from"
                render={({ field }) => (
                  <DayPickerField
                    label={offerSeriesCopy.rangeFromLabel}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
              <FormField
                control={form.control}
                name="range_to"
                render={({ field }) => (
                  <DayPickerField
                    label={offerSeriesCopy.rangeToLabel}
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </div>

            <LGoButton
              type="button"
              variant="secondary"
              onClick={buildPreview}
              disabled={!canDescribePattern}
            >
              {rows === null ? offerSeriesCopy.buildPreview : offerSeriesCopy.rebuildPreview}
            </LGoButton>
          </div>

          {rows !== null && (
            <FieldGroup
              icon={<ListChecks className="h-3.5 w-3.5" />}
              title={offerSeriesCopy.previewSection}
            >
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">{offerSeriesCopy.rowsHint}</p>
                {rows.length === 0 && (
                  <p className="text-sm text-muted-foreground">{offerSeriesCopy.emptyPreview}</p>
                )}
                <ul className="divide-y divide-border rounded-lg border border-border">
                  {rows.map((row) => (
                    <li
                      key={row.id}
                      className={cn(
                        "flex flex-wrap items-center gap-3 p-3",
                        refusedRowIds.has(row.id) && "bg-destructive/10",
                      )}
                    >
                      <span className="w-40 text-sm font-medium">
                        {formatDate(row.startTime, "EEEE d. M. yyyy")}
                      </span>
                      <TimePicker
                        ariaLabel={`${offerSeriesCopy.slotStartLabel} ${formatDate(row.startTime, "d. M. yyyy")}`}
                        className="w-32"
                        value={rowTime(row.startTime)}
                        onChange={(value) => correctRow(row.id, { startTime: value })}
                      />
                      <TimePicker
                        ariaLabel={`${offerSeriesCopy.slotEndLabel} ${formatDate(row.startTime, "d. M. yyyy")}`}
                        className="w-32"
                        value={rowTime(row.endTime)}
                        onChange={(value) => correctRow(row.id, { endTime: value })}
                      />
                      {(rowTime(row.startTime) !== patternStartTime ||
                        rowTime(row.endTime) !== patternEndTime) && (
                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-foreground">
                          {offerSeriesCopy.rowEdited}
                        </span>
                      )}
                      {endsOnAnotherDay(row) && (
                        <span className="text-sm text-muted-foreground">
                          {offerSeriesCopy.slotEndsNextDay(formatDate(row.endTime, "dd. MM."))}
                        </span>
                      )}
                      <span className="text-sm text-muted-foreground">
                        {row.saleOpensAt
                          ? offerSeriesCopy.rowSaleFrom(formatDate(row.saleOpensAt, "d. M. HH:mm"))
                          : offerSeriesCopy.rowSaleDefault}
                      </span>
                      {row.saleClosesAt && (
                        <span className="text-sm text-muted-foreground">
                          {offerSeriesCopy.rowSaleUntil(formatDate(row.saleClosesAt, "d. M. HH:mm"))}
                        </span>
                      )}
                      <LGoButton
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="ml-auto"
                        aria-label={`Odebrat termín ${formatDate(row.startTime, "d. M. yyyy")}`}
                        onClick={() => removeRow(row.id)}
                        iconLeft={<Trash2 />}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            </FieldGroup>
          )}
        </div>
      </Form>
    </LGoSheet>
  );
}

function endsOnAnotherDay(row: PlannedOffer): boolean {
  return dayKey(row.endTime) !== dayKey(row.startTime);
}

function InheritedFromTemplate({ template }: { template: OfferActivityOption }) {
  const templateClose = saleCloseOf(template);
  const rows: { label: string; value: string }[] = [
    {
      label: offerSeriesCopy.priceLabel,
      value:
        template.defaultPriceDiscounted === null
          ? offerSeriesCopy.missingPrice
          : `${template.defaultPriceDiscounted} Kč`,
    },
    {
      label: offerSeriesCopy.capacityLabel,
      value:
        template.defaultCapacity === null
          ? offerSeriesCopy.missingCapacity
          : String(template.defaultCapacity),
    },
    {
      label: offerSeriesCopy.saleStartLabel,
      value:
        template.saleLeadTimeHours === null
          ? offerSeriesCopy.saleStartDefault
          : offerSeriesCopy.saleStartFromTemplate(template.saleLeadTimeHours),
    },
    {
      label: offerSeriesCopy.saleEndLabel,
      value: templateClose
        ? offerSeriesCopy.saleEndFromTemplate(templateClose)
        : offerSeriesCopy.saleEndDefault,
    },
  ];

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-border bg-muted/40 p-4">
      <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        <Link2 className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        {offerSeriesCopy.inheritedHeading}
      </p>
      <dl className="space-y-1 text-sm">
        {rows.map(({ label, value }) => (
          <div key={label} className="flex justify-between gap-3">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="text-foreground">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

interface DayPickerFieldProps {
  label: string;
  value: Date;
  onChange: (day: Date) => void;
}

function DayPickerField({ label, value, onChange }: DayPickerFieldProps) {
  return (
    <FormItem className="flex flex-col">
      <FormLabel>{label}</FormLabel>
      <FormControl>
        <LGoDayPicker value={value} onChange={onChange} />
      </FormControl>
      <FormMessage />
    </FormItem>
  );
}
